import { type Ref } from "vue";
// import type productGalleryItem from "./ProductGalleryItem.vue";
// import { elementVisible } from "./element-intersection";
import { type GalleryItem } from "./types";
// type ItemsElType = Ref<InstanceType<typeof productGalleryItem>[]>;

export function createScrollController(
    frame: Ref<HTMLUListElement | undefined>,
    itemsEl: Ref<GalleryItem[]>,
    el: Ref<HTMLElement | undefined>
) {
    return {
        scrollRight: () => scrollRight(frame, itemsEl, el),
        scrollLeft: () => scrollLeft(frame, itemsEl, el),
        dragStart: (e: Event) => dragStart(e, frame, el),
    };
}

function scrollRight(
    frame: Ref<HTMLUListElement | undefined>,
    itemsEl: Ref<GalleryItem[]>,
    el: Ref<HTMLElement | undefined>
) {
    if (frame.value == null) throw new Error("missing frame");
    const items = itemsEl.value;
    const { lastFullyVisible, firstFullyVisible } =
        getFirstAndLastFullyVisibleItem(items);
    if (lastFullyVisible == null) {
        throw new Error("no element");
    }
    let nextElement = lastFullyVisible;
    if (nextElement === firstFullyVisible) {
        const nextElem = items[items.indexOf(nextElement) + 1];
        if (nextElem != null) {
            nextElement = nextElem;
        }
    }
    //console.log("snap to item " + sizes.indexOf(nextElement));
    scrollToEl(frame, getElement(el.value!, nextElement));
    // nextElement.el.style.background = "red";
}

function getElement(el: HTMLElement, item: GalleryItem) {
    return el.querySelector(
        "[data-item-type='product'][data-item-key='" + item.key + "']"
    ) as HTMLElement;
}

function scrollLeft(
    frame: Ref<HTMLUListElement | undefined>,
    itemsEl: Ref<GalleryItem[]>,
    el: Ref<HTMLElement | undefined>
) {
    if (frame.value == null) throw new Error("missing frame");
    const items = itemsEl.value;
    const { lastFullyVisible, firstFullyVisible } =
        getFirstAndLastFullyVisibleItem(items);
    if (firstFullyVisible == null) {
        throw new Error("no element");
    }
    if (lastFullyVisible == null) {
        throw new Error("no element");
    }

    let prevElement = firstFullyVisible;
    if (prevElement === lastFullyVisible) {
        const prevElem = items[items.indexOf(prevElement) - 1];
        if (prevElem != null) {
            prevElement = prevElem;
        }
    }

    const lastFullyVisibleDOMElement = getElement(el.value!, lastFullyVisible);
    const firstFullyVisibleDOMElement = getElement(
        el.value!,
        firstFullyVisible
    );

    const distance =
        lastFullyVisibleDOMElement.offsetLeft -
        firstFullyVisibleDOMElement.offsetLeft;

    //console.log("snap to item " + sizes.indexOf(firstFullyVisible));
    scrollToEl(frame, getElement(el.value!, prevElement), distance);
}

export function getFirstAndLastFullyVisibleItem(items: GalleryItem[]) {
    //const sizes = getSizes(itemsEl);
    //console.log(sizes.map((x) => x.visible).join(","));
    //console.log(sizes.map((x) => x.parentIndex).join(","));
    const highest = getHighest(items);
    const firstFullyVisible = items.find((i) => i.intersection === highest);
    const lastFullyVisible = findLast(items, (i) => i.intersection === highest);

    return { firstFullyVisible, lastFullyVisible };
}

function findLast<T>(array: T[], predicate: (o: T) => boolean): T | undefined {
    return array
        .concat()
        .reverse()
        .find((x) => predicate(x));
}

// function getSizes(itemsEl: ItemsElType) {
//     if (itemsEl.value == null) throw new Error("missing itemsEl");
//     const sizes = itemsEl.value
//         .map((vnode) => {
//             let elVizPercent = elementVisible(vnode.$el as HTMLElement);
//             if (isNaN(elVizPercent)) elVizPercent = 0;
//             const visible = Math.round(100 * elVizPercent);
//             const el = vnode.$el as HTMLElement;
//             return {
//                 vnode,
//                 el,
//                 intersection: elVizPercent,
//                 parentIndex: Array.from(el.parentElement!.children).indexOf(el),
//                 visible,
//             };
//         })
//         .sort((a, b) => {
//             if (a.parentIndex === b.parentIndex) return 0;
//             if (a.parentIndex === -1) {
//                 return 1;
//             }
//             if (b.parentIndex === -1) {
//                 return -1;
//             }
//             return a.parentIndex - b.parentIndex;
//         });
//     return sizes;
// }

function getHighest(sizes: GalleryItem[]) {
    const highest = sizes.reduce(
        (prev, curr) =>
            prev > (curr.intersection ?? 0) ? prev : curr.intersection ?? 0,
        0
    );
    return highest;
}

function setScroll(el: HTMLUListElement, value: number) {
    el.scrollLeft = value;
}

function scrollToEl(
    frame: Ref<HTMLUListElement | undefined>,
    target: HTMLElement,
    distance = 0
) {
    if (frame.value == null) throw new Error("missing frame");
    setScroll(frame.value, target.offsetLeft - distance);
}

function dragStart(
    e: Event,
    frame: Ref<HTMLUListElement | undefined>,
    el: Ref<HTMLElement | undefined>
) {
    //console.log("dragStart");
    const ev = e as DragEvent;
    if (ev.buttons !== 1) {
        console.debug("Cancelled dragStart: Left button is not pressed");
        return;
    }
    if (frame.value == null) {
        console.debug("Cancelled dragStart: Frame ref is missing");
        return;
    }
    if (el.value == null) {
        console.debug("Cancelled dragStart: El ref is missing");
        return;
    }
    ev.preventDefault();
    const containerEl = el.value;
    const frameEl = frame.value;
    const cancelCtrl = new AbortController();
    const startX = ev.pageX;
    const startScroll = frameEl.scrollLeft;

    //const targetLink = getParentLink(ev.target);

    // if (targetLink instanceof HTMLAnchorElement) {
    //     console.debug("bind click to prevent clickLink", targetLink);
    //     targetLink.addEventListener(
    //         "click",
    //         (ev) => {
    //             console.debug("PREVENTING CLICK");
    //             ev.preventDefault();
    //         },
    //         {
    //             once: true,
    //             signal: cancelCtrl.signal,
    //         }
    //     );
    // }

    const unbindClickDisablerCtrl = new AbortController();

    cancelCtrl.signal.addEventListener("abort", () => {
        setTimeout(() => unbindClickDisablerCtrl.abort(), 50);
    });

    containerEl.addEventListener(
        "click",
        (ev) => {
            ev.preventDefault();
            console.debug("prevented link click!");
        },
        {
            once: true,
            signal: unbindClickDisablerCtrl.signal,
        }
    );

    const originalScrollBehavior = frameEl.style.scrollBehavior;

    frameEl.style.scrollBehavior = "initial";

    cancelCtrl.signal.addEventListener("abort", () => {
        frameEl.style.scrollBehavior = originalScrollBehavior;
        //console.log("stopped dragEvents");
    });

    window.addEventListener(
        "mousemove",
        (ev) => {
            if (ev.buttons !== 1) {
                console.debug("mousemove UP");
                cancelCtrl.abort();
                return;
            }
            setScroll(frameEl, startScroll + (startX - ev.pageX));
        },
        { signal: cancelCtrl.signal }
    );
    window.addEventListener(
        "mouseup",
        () => {
            console.debug("mouseup");
            cancelCtrl.abort();
        },
        { signal: cancelCtrl.signal }
    );
    //console.log("bound dragStart");
}
